/**
 * Der Abschnitt „Ruckel-Test“ unter Einstellungen → Erweitert. (02.10.2026, Ticket 2912)
 *
 * Martin, iPhone: Das Scrollen ruckelt überall, auch in den Einstellungen. Geraten wurde genug.
 * Hier schaltet er am Gerät einzelne Verdächtige ab (Animationen, Unschärfe, Schatten, Leisten,
 * Wachen, Live-Aktualisierungen, Wachhalte-Video, Dock-Unschärfe, Hintergrund, Ebenen) und sieht in
 * Sekunden, bei welchem es flüssig wird. Dann bauen wir genau das ab.
 *
 * Nur Konto 1. Die Schalter wirken sofort (kein Neuladen) und überleben es (localStorage; ohne läuft
 * alles weiter). Der Stand und seine Wirkung: lib/ruckel-schalter.ts und der Block „Ruckel-Test“ am
 * Ende von style.css. Hier steht nur die Oberfläche und die Messung.
 *
 * ## „Messung senden“
 *
 * Mit „Scroll-Messfühler aus“ misst sonst NIEMAND die Bildzeit (lib/scroll-takt.ts schweigt dann).
 * Darum hat der Knopf sein eigenes Fenster: zehn Sekunden lang zählt er die Bilder, die beim Scrollen
 * gezeichnet werden, und schickt am Ende EINE Zeile
 *
 *     [scroll-t] art=ruckeltest schalter=… takt-p50=… luecken100=… luecken50=… max=… bilder=… ereign=…
 *
 * auf demselben Weg wie die anderen `[scroll-t]`-Zeilen. Keine Inhalte: Schalternamen und Zahlen.
 *
 * ⚠️ Die Messung ist nur AN, solange das Fenster läuft (Zähler sichtbar). Sie sieht nicht, ob das Bild
 *    wirklich fertig gezeichnet wurde (rAF kennt das Zeichnen selbst nicht) — dasselbe Maß wie scroll-takt.
 */
import { meldeSpur } from "./mikro-spur.js";
import {
  RUCKEL_SCHALTER,
  beiRuckelAenderung,
  ruckelAus,
  ruckelStandText,
  ruckelZuruecksetzen,
  setzeRuckel,
  setzeRuckelAlle,
} from "./ruckel-schalter.js";

/** So lange läuft das Messfenster. */
export const MESS_MS = 10_000;
/** Ein Bild zählt als „beim Scrollen“, wenn das letzte Scroll-Ereignis höchstens so lange her ist. */
const STILL_MS = 250;
/** Lücken ab so viel Millisekunden: 3 / 6 ausgefallene Bilder bei 60 Hz. */
const LUECKE_50_MS = 50;
const LUECKE_100_MS = 100;

/** Ein einzelner Wisch: zusammenhängende Bilder beim Scrollen. */
interface WischWerte {
  bilder: number;
  max: number;
  luecken100: number;
}

/** Ab so vielen Bildern zählt ein Stück Scrollen als Wisch (kürzere sind Antippen oder Auslaufen). */
export const WISCH_MIN_BILDER = 5;

/** Die Zahlen EINES Wischs aus seinen Bildabständen. */
function rechneWisch(abstaende: readonly number[]): WischWerte {
  return {
    bilder: abstaende.length,
    max: Math.round(Math.max(0, ...abstaende)),
    luecken100: abstaende.filter((a) => a >= LUECKE_100_MS).length,
  };
}

export interface Messwerte {
  /** Bilder, deren Vorgänger auch beim Scrollen gezeichnet wurde. */
  bilder: number;
  ereignisse: number;
  p50: number | null;
  max: number | null;
  luecken50: number;
  luecken100: number;
  /** Der LETZTE Wisch im Fenster (mindestens `WISCH_MIN_BILDER` Bilder), sonst null. */
  letzter: WischWerte | null;
  /** Größte Bildlücke eines einzelnen Wischs im Fenster (der schlimmste Wisch), sonst null. */
  schlimmstMax: number | null;
}

/** Aus den Abständen zwischen den Bildern (ms) die Zahlen. Reine Rechnung. */
export function rechneMessung(abstaende: readonly number[], ereignisse: number, wische: ReadonlyArray<readonly number[]> = []): Messwerte {
  const n = abstaende.length;
  const echte = wische.filter((w) => w.length >= WISCH_MIN_BILDER).map(rechneWisch);
  const letzter = echte.length ? (echte[echte.length - 1] ?? null) : null;
  const schlimmstMax = echte.length ? Math.max(...echte.map((w) => w.max)) : null;
  if (n === 0) return { bilder: 0, ereignisse, p50: null, max: null, luecken50: 0, luecken100: 0, letzter, schlimmstMax };
  const sortiert = [...abstaende].sort((a, b) => a - b);
  const mitte = Math.floor(n / 2);
  const median = n % 2 ? (sortiert[mitte] ?? 0) : ((sortiert[mitte - 1] ?? 0) + (sortiert[mitte] ?? 0)) / 2;
  return {
    bilder: n,
    ereignisse,
    p50: Math.round(median * 10) / 10,
    max: Math.round(sortiert[n - 1] ?? 0),
    luecken50: abstaende.filter((a) => a >= LUECKE_50_MS).length,
    luecken100: abstaende.filter((a) => a >= LUECKE_100_MS).length,
    letzter,
    schlimmstMax,
  };
}

/**
 * Die Zeile fürs Protokoll. Ohne Scrollen gibt es keine Zahlen (`-`) — die Zeile wird dann gar nicht
 * erst geschickt, siehe `messeUndSende`. `ansicht`: der Name der offenen Ansicht.
 */
export function messZeile(stand: string, w: Messwerte, dauerS: number, ansicht: string): string {
  const zahl = (x: number | null): string => (x === null ? "-" : String(x));
  return (
    `art=ruckeltest schalter=${stand} takt-p50=${zahl(w.p50)} luecken100=${w.luecken100} luecken50=${w.luecken50} ` +
    `max=${zahl(w.max)} bilder=${w.bilder} ereign=${w.ereignisse} dauer=${dauerS} ansicht=${ansicht} ` +
    `wisch-max=${zahl(w.letzter?.max ?? null)} wisch-l100=${zahl(w.letzter?.luecken100 ?? null)} wisch-bilder=${zahl(w.letzter?.bilder ?? null)} ` +
    `wisch-schlimmst=${zahl(w.schlimmstMax)}`
  );
}

/**
 * Zehn Sekunden Bildabstände sammeln, solange gescrollt wird. Gibt `abbrechen` zurück.
 * `fertig` kommt genau einmal (auch nach Abbruch, dann mit `abgebrochen`).
 */
export function messeFenster(
  dauerMs: number,
  beiTick: (restMs: number) => void,
  fertig: (w: Messwerte, abgebrochen: boolean) => void,
): () => void {
  const abstaende: number[] = [];
  const wische: number[][] = [];
  let aktuell: number[] = [];
  let ereignisse = 0;
  let letzterScroll = -Infinity;
  let vorher: number | null = null;
  let vorherBeimScrollen = false;
  let raf = 0;
  let aus = false;
  const start = performance.now();

  const beiScroll = (): void => {
    ereignisse++;
    letzterScroll = performance.now();
  };
  const beende = (abgebrochen: boolean): void => {
    if (aus) return;
    aus = true;
    document.removeEventListener("scroll", beiScroll, true);
    document.removeEventListener("visibilitychange", beiSicht);
    cancelAnimationFrame(raf);
    window.clearInterval(tick);
    if (aktuell.length) wische.push(aktuell);
    fertig(rechneMessung(abstaende, ereignisse, wische), abgebrochen);
  };
  const beiSicht = (): void => {
    if (document.visibilityState !== "visible") beende(true);
  };
  const bild = (t: number): void => {
    if (aus) return;
    const beimScrollen = t - letzterScroll <= STILL_MS;
    if (vorher !== null && vorherBeimScrollen && beimScrollen) {
      abstaende.push(t - vorher);
      aktuell.push(t - vorher);
    }
    // Ein Bild ohne Scrollen schließt den Wisch ab.
    if (!beimScrollen && aktuell.length) {
      wische.push(aktuell);
      aktuell = [];
    }
    vorher = t;
    vorherBeimScrollen = beimScrollen;
    if (t - start >= dauerMs) {
      beende(false);
      return;
    }
    raf = requestAnimationFrame(bild);
  };
  const tick = window.setInterval(() => {
    beiTick(Math.max(0, dauerMs - (performance.now() - start)));
  }, 250);

  document.addEventListener("scroll", beiScroll, { capture: true, passive: true });
  document.addEventListener("visibilitychange", beiSicht);
  raf = requestAnimationFrame(bild);
  beiTick(dauerMs);
  return () => beende(true);
}

function knopf(text: string, klasse = ""): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = `settings-btn ruckel-knopf ${klasse}`.trim();
  b.textContent = text;
  return b;
}

/** Eine Zeile je Schalter: Nummer, Name, Beschreibung, Haken. Ganze Zeile ist die Berührungsfläche. */
function baueZeile(nr: number, s: (typeof RUCKEL_SCHALTER)[number]): { zeile: HTMLLabelElement; haken: HTMLInputElement } {
  const zeile = document.createElement("label");
  zeile.className = "settings-field settings-field-toggle ruckel-zeile";
  zeile.title = s.hinweis;
  zeile.dataset.schalter = s.id;

  const nummer = document.createElement("span");
  nummer.className = "ruckel-nr";
  nummer.setAttribute("aria-hidden", "true");
  nummer.textContent = String(nr);

  const text = document.createElement("span");
  text.className = "ruckel-text";
  const name = document.createElement("span");
  name.className = "settings-label ruckel-name";
  name.textContent = s.name;
  const hinweis = document.createElement("span");
  hinweis.className = "settings-field-hint ruckel-hinweis";
  hinweis.id = `ruckel-hinweis-${s.id}`;
  hinweis.textContent = s.hinweis;
  text.append(name, hinweis);

  const haken = document.createElement("input");
  haken.type = "checkbox";
  haken.setAttribute("role", "switch");
  haken.setAttribute("aria-describedby", hinweis.id);
  haken.setAttribute("aria-label", `Schalter ${nr}: ${s.name}`);
  haken.checked = ruckelAus(s.id);
  zeile.classList.toggle("is-an", haken.checked);
  haken.addEventListener("change", () => setzeRuckel(s.id, haken.checked));

  zeile.append(nummer, text, haken);
  return { zeile, haken };
}

/** Der Abschnitt. Eigene Funktion statt Klasse: ein Kasten, ein Zustand, kein Aufräumen nötig. */
export function baueRuckelTestKarte(): HTMLElement {
  const karte = document.createElement("section");
  karte.className = "settings-subsection ruckel-test";
  karte.setAttribute("aria-labelledby", "ruckel-titel");

  const titel = document.createElement("h3");
  titel.className = "settings-subsection-title";
  titel.id = "ruckel-titel";
  titel.textContent = "Ruckel-Test";

  const einleitung = document.createElement("p");
  einleitung.className = "settings-field-hint ruckel-einleitung";
  einleitung.textContent =
    "Jeder Schalter schaltet etwas ab, das das Scrollen bremsen könnte. Er wirkt sofort und bleibt nach dem Neuladen. Nur für dieses Konto sichtbar.";

  const liste = document.createElement("div");
  liste.className = "ruckel-liste";
  const haken = new Map<string, HTMLInputElement>();
  RUCKEL_SCHALTER.forEach((s, i) => {
    const z = baueZeile(i + 1, s);
    haken.set(s.id, z.haken);
    liste.append(z.zeile);
  });

  const stand = document.createElement("p");
  stand.className = "settings-field-hint ruckel-stand";
  stand.setAttribute("role", "status");
  const zeigeStand = (): void => {
    const an = RUCKEL_SCHALTER.filter((s) => ruckelAus(s.id)).length;
    stand.textContent = an === 0 ? "Alles an wie im Alltag." : `${an} von ${RUCKEL_SCHALTER.length} Schaltern ein.`;
  };
  zeigeStand();

  const abmelden = beiRuckelAenderung((id, an) => {
    if (!karte.isConnected) {
      abmelden();
      return;
    }
    const h = haken.get(id);
    if (h) {
      h.checked = an;
      h.closest(".ruckel-zeile")?.classList.toggle("is-an", an);
    }
    zeigeStand();
  });

  const reihe = document.createElement("div");
  reihe.className = "ruckel-reihe";
  const alleAus = knopf("Alle aus", "ruckel-alle");
  alleAus.title = "Schaltet alle Schalter oben ein: alles abgeschaltet, was bremsen könnte.";
  alleAus.addEventListener("click", () => setzeRuckelAlle(true));
  const zurueck = knopf("Zurücksetzen", "ruckel-zurueck");
  zurueck.title = "Alle Schalter wieder aus: alles läuft wie im Alltag.";
  zurueck.addEventListener("click", () => ruckelZuruecksetzen());
  reihe.append(alleAus, zurueck);

  const anleitung = document.createElement("p");
  anleitung.className = "ruckel-anleitung";
  anleitung.textContent = "Scrolle in den Einstellungen 10 Sekunden mit allem aus. Ruckelt es noch? Dann schalte einzeln wieder ein.";

  const messen = knopf("Messung senden", "ruckel-messen");
  messen.title = "Zehn Sekunden Bildzeit beim Scrollen messen und mit dem Schalterstand ans Protokoll schicken (keine Inhalte).";
  const meldung = document.createElement("p");
  meldung.className = "settings-status ruckel-meldung";
  meldung.setAttribute("role", "status");
  meldung.setAttribute("aria-live", "polite");

  let laufendes: (() => void) | null = null;
  messen.addEventListener("click", () => {
    if (laufendes) return;
    messen.disabled = true;
    laufendes = messeFenster(
      MESS_MS,
      (rest) => {
        if (!karte.isConnected) {
          laufendes?.();
          return;
        }
        meldung.textContent = `Scrolle jetzt … noch ${Math.ceil(rest / 1000)} s`;
      },
      (w, abgebrochen) => {
        laufendes = null;
        messen.disabled = false;
        if (abgebrochen) {
          meldung.textContent = "Messung abgebrochen.";
          return;
        }
        if (w.bilder === 0) {
          meldung.textContent = "Es wurde nicht gescrollt — nichts gesendet. Noch einmal, und diesmal wischen.";
          return;
        }
        const ansicht = document.body.dataset.activeView ?? "-";
        meldeSpur("scroll-t", messZeile(ruckelStandText(), w, MESS_MS / 1000, ansicht));
        meldung.textContent = `Gesendet: Bildabstand ${w.p50} ms (Mitte), ${w.luecken100} Lücken über 100 ms.`;
      },
    );
  });

  karte.append(titel, einleitung, liste, stand, reihe, anleitung, messen, meldung);
  return karte;
}

/** Für die Einstellungsseite „Erweitert“: der Abschnitt nur für Konto 1, sonst nichts. */
export function ruckelTestKarten(): HTMLElement[] {
  const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
  return uid === 1 ? [baueRuckelTestKarte()] : [];
}
